// Local-first Open-Play state: persisted to localStorage, offline-resilient.
// Includes the fairness / sit-to-play matching engine.

const STORAGE_KEY = "pointcontrol_openplay_v2";
const INDEX_KEY = "pointcontrol_openplay_index_v1";
const CURRENT_KEY = "pointcontrol_openplay_current_v1";

const FAIRNESS_WEIGHT = 1;

// rest-time thresholds (seconds) for queue color coding
export const REST_THRESHOLDS = { justFinished: 120, warmingUp: 300 };

// Rotation systems (see playpickleball.com/types-of-pickleball-rec-play)
export const ROTATIONS = [
  {
    id: "open_play_plus",
    name: "Open Play +",
    tagline: "First-come, no repeat partners or opponents",
    info: "First-come, first-served like Open Play, but the engine avoids pairing you with anyone you've already partnered with — and just as strongly avoids putting you against opponents you've already faced. Once everyone available has been paired and played, match-ups may repeat.",
  },
  {
    id: "open_play",
    name: "Open Play",
    tagline: "First-come, first-served",
    info: "Classic paddle-rack style. Players rotate on a fairness queue — whoever has rested longest is staged next. After each game both teams return to the back of the line.",
  },
  {
    id: "king_of_court",
    name: "King of the Court",
    tagline: "Winners move up, losers move down",
    info: "Also called Ladder / Waterfall. The winning team is pushed to the front of the queue (moving 'up' a court) while the losing team drops to the back. A recorded winner is required each game.",
  },
  {
    id: "challenge_court",
    name: "Challenge Court",
    tagline: "Winners stay on together",
    info: "Winners keep the court and stay together as a team while fresh challengers are pulled from the queue to face them. Losers head to the back of the line. Requires a recorded winner.",
  },
  {
    id: "round_robin",
    name: "Round Robin",
    tagline: "Play with everyone",
    info: "Maximises variety — the engine avoids re-pairing you with your last partner so you rotate through as many different partners and opponents as possible. Both teams re-queue after each game.",
  },
  {
    id: "numbering",
    name: "Numbering",
    tagline: "Random draw each round",
    info: "The simplest system — every round players are shuffled and drawn at random, ignoring skill and rest. Great for casual fun where anyone can end up with anyone.",
  },
];

export function rotationMeta(id) {
  return ROTATIONS.find((r) => r.id === id) || ROTATIONS[0];
}

function uid(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}${Date.now().toString(36).slice(-4)}`;
}

// Scoring modes (Module 1 upgrade)
export const SCORING_MODES = {
  casual: {
    id: "casual",
    label: "Casual Mode",
    subtext: "Tracks Win Rate & Strength of Schedule (SoS) on player rankings.",
  },
  advanced: {
    id: "advanced",
    label: "Advanced Mode",
    subtext: "Autofills game timers to rank players by Points Per Minute (PPM) & Differential Rates.",
  },
};

// New metric fields default safely to 0 / [] so old sessions rehydrate cleanly.
const PLAYER_METRIC_DEFAULTS = {
  wins: 0, losses: 0, winRate: 0,
  pointsScored: 0, pointsConceded: 0, pointDifferential: 0,
  strengthOfSchedule: 0, opponentHistoryIds: [], partnerHistoryIds: [], lastOpponentIds: [], totalRestTime: 0,
  totalMatchMinutesPlayed: 0, avgPPMDiff: 0, avgSDR: 0, avgPPMC: 0,
};

export function normalizePlayer(p) {
  const merged = { ...PLAYER_METRIC_DEFAULTS, ...p };
  merged.opponentHistoryIds = Array.isArray(p?.opponentHistoryIds) ? p.opponentHistoryIds : [];
  merged.partnerHistoryIds = Array.isArray(p?.partnerHistoryIds) ? p.partnerHistoryIds : [];
  merged.lastOpponentIds = Array.isArray(p?.lastOpponentIds) ? p.lastOpponentIds : [];
  merged.checkedOut = !!merged.checkedOut;
  return merged;
}

// a player counts as a session participant once they've checked in (even if later checked out)
export function hasParticipated(p) {
  return !!(p && (p.checkedIn || p.checkedOut || (p.gamesPlayed || 0) > 0));
}
export function countParticipants(state) {
  return state.players.filter(hasParticipated).length;
}
export function checkedOutPlayers(state) {
  return state.players.filter((p) => !p.checkedIn && p.checkedOut);
}

function normalizeState(s) {
  return { ...s, players: (s.players || []).map(normalizePlayer) };
}

// dynamic profile tag for Advanced Mode based on pacing telemetry
export function advancedTag(p) {
  if (!p || (p.gamesPlayed || 0) === 0) return null;
  if ((p.avgSDR || 0) >= 1 && (p.avgPPMDiff || 0) > 0) return "Aggressive & Fast";
  if ((p.avgPPMDiff || 0) > 0) return "Steady Scorer";
  if ((p.avgPPMC || 0) > 0 && (p.avgPPMC || 0) <= 1) return "Defensive Grind";
  return "Grinder";
}

// per-mode ranking badges shown next to a player's name
export function metricSummary(p, mode) {
  const np = normalizePlayer(p || {});
  if (mode === "advanced") {
    return {
      primary: `${(np.avgSDR || 0) >= 0 ? "+" : ""}${(np.avgSDR || 0).toFixed(2)} SDR`,
      secondary: `${(np.avgPPMDiff || 0).toFixed(1)} PPM`,
      tag: advancedTag(np),
    };
  }
  return {
    primary: `WR ${Math.round((np.winRate || 0) * 100)}%`,
    secondary: `SoS ${Math.round(((np.strengthOfSchedule || 0) / 5) * 100)}%`,
    tag: null,
  };
}

function defaultState() {
  const now = Date.now();
  return {
    session: { eventName: "", location: "", started: false, shareToken: null },
    players: [],
    courts: [
      { id: uid("court"), name: "Court 1", status: "available", currentMatch: null, startTime: null },
      { id: uid("court"), name: "Court 2", status: "available", currentMatch: null, startTime: null },
    ],
    queue: [], // ordered player ids waiting
    settings: { mode: "doubles", balanced: true, rotation: "open_play_plus", liveShowStandings: false, pairRankingInclusion: true, scoringMode: "casual", winningScoreThreshold: 11, gamesTarget: 0, minGamesToRank: 5 },
    pendingTogether: [], // challenge-court winners kept together next stage
    history: [], // completed match log (local-first)
    _createdAt: now,
  };
}

// storage is namespaced per-user so accounts never share a session on one device;
// within a namespace, multiple independent sessions live side by side (multi-session).
let NS = "anon";
let CUR = null;
export function setStorageNamespace(ns) { NS = ns || "anon"; CUR = null; }

function indexKey() { return `${INDEX_KEY}:${NS}`; }
function currentKey() { return `${CURRENT_KEY}:${NS}`; }
function stateKey(id) { return `${STORAGE_KEY}:${NS}:${id}`; }
function legacyKey() { return `${STORAGE_KEY}:${NS}`; }

function newSessionId() { return uid("sess"); }

function readIndex() {
  try { return JSON.parse(localStorage.getItem(indexKey())) || []; } catch (e) { return []; }
}
function writeIndex(ids) {
  try { localStorage.setItem(indexKey(), JSON.stringify(ids)); } catch (e) { /* storage unavailable */ }
}

// one-time migration: fold a pre-multi-session single state into a session slot.
function migrateLegacy() {
  if (readIndex().length) return;
  try {
    const raw = localStorage.getItem(legacyKey());
    if (raw) {
      const id = newSessionId();
      localStorage.setItem(stateKey(id), raw);
      writeIndex([id]);
      localStorage.setItem(currentKey(), id);
      localStorage.removeItem(legacyKey());
    }
  } catch (e) { /* ignore */ }
}

// resolve (and if needed create) the current session id.
function ensureCurrent() {
  migrateLegacy();
  let cur = CUR || (() => { try { return localStorage.getItem(currentKey()); } catch (e) { return null; } })();
  const idx = readIndex();
  if (!cur || !idx.includes(cur)) {
    if (idx.length) {
      cur = idx[0];
    } else {
      cur = newSessionId();
      writeIndex([cur]);
      try { localStorage.setItem(stateKey(cur), JSON.stringify(defaultState())); } catch (e) { /* ignore */ }
    }
    try { localStorage.setItem(currentKey(), cur); } catch (e) { /* ignore */ }
  }
  CUR = cur;
  return cur;
}

export function loadState() {
  const id = ensureCurrent();
  try {
    const raw = localStorage.getItem(stateKey(id));
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw);
    if (!parsed.players || !parsed.courts) return defaultState();
    return normalizeState(parsed);
  } catch (e) {
    return defaultState();
  }
}

export function saveState(state) {
  const id = ensureCurrent();
  try {
    localStorage.setItem(stateKey(id), JSON.stringify(state));
  } catch (e) {
    /* storage full / unavailable */
  }
}

export function resetState() {
  const s = defaultState();
  saveState(s);
  return s;
}

// ---- multi-session registry ------------------------------------------------
export function currentSessionId() { return ensureCurrent(); }

export function listSessions() {
  const cur = ensureCurrent();
  return readIndex().map((id) => {
    let s = null;
    try { const raw = localStorage.getItem(stateKey(id)); s = raw ? JSON.parse(raw) : null; } catch (e) { s = null; }
    const ok = s && s.players && s.courts ? s : null;
    const players = ok?.players || [];
    return {
      id,
      current: id === cur,
      eventName: ok?.session?.eventName || "",
      location: ok?.session?.location || "",
      started: !!ok?.session?.started,
      startedAt: ok?.session?.startedAt || null,
      playerCount: players.length,
      checkedIn: players.filter((p) => p.checkedIn).length,
      courtCount: (ok?.courts || []).length,
      inUse: (ok?.courts || []).filter((c) => c.status === "in_use").length,
      matches: (ok?.history || []).length,
      mode: ok?.settings?.mode || "doubles",
      rotation: ok?.settings?.rotation || "open_play_plus",
    };
  });
}

export function createSession() {
  ensureCurrent();
  const id = newSessionId();
  const idx = readIndex();
  idx.push(id);
  writeIndex(idx);
  const s = defaultState();
  try { localStorage.setItem(stateKey(id), JSON.stringify(s)); } catch (e) { /* ignore */ }
  try { localStorage.setItem(currentKey(), id); } catch (e) { /* ignore */ }
  CUR = id;
  return { id, state: s };
}

export function switchSession(id) {
  if (!readIndex().includes(id)) return loadState();
  try { localStorage.setItem(currentKey(), id); } catch (e) { /* ignore */ }
  CUR = id;
  return loadState();
}

export function deleteSession(id) {
  const idx = readIndex().filter((x) => x !== id);
  writeIndex(idx);
  try { localStorage.removeItem(stateKey(id)); } catch (e) { /* ignore */ }
  let curStored = null;
  try { curStored = localStorage.getItem(currentKey()); } catch (e) { curStored = null; }
  if (CUR === id || curStored === id) {
    CUR = null;
    try { localStorage.removeItem(currentKey()); } catch (e) { /* ignore */ }
  }
  return loadState();
}

// overwrite local state with a restored full state (used to resume an event)
export function hydrateState(state) {
  const safe = state && state.players && state.courts ? normalizeState(state) : defaultState();
  saveState(safe);
  return safe;
}

// ---- session lifecycle -----------------------------------------------------
export function startSession(state, { eventName, location, mode, rotation, scoringMode, winningScoreThreshold, gamesTarget, minGamesToRank, balanced }) {
  return {
    ...state,
    session: {
      eventName: (eventName || "").trim() || "Open Play Session",
      location: (location || "").trim(),
      started: true,
      startedAt: Date.now(),
      shareToken: uid("live"), // fresh spectator link every event
    },
    settings: {
      ...state.settings,
      mode: mode || state.settings.mode,
      rotation: rotation || state.settings.rotation,
      scoringMode: scoringMode || state.settings.scoringMode || "casual",
      winningScoreThreshold: Number(winningScoreThreshold) || state.settings.winningScoreThreshold || 11,
      gamesTarget: Number(gamesTarget) || 0,
      minGamesToRank: minGamesToRank == null ? (state.settings.minGamesToRank ?? 5) : Math.max(0, Number(minGamesToRank) || 0),
      balanced: balanced == null ? (state.settings.balanced ?? true) : !!balanced,
    },
  };
}

// Games-target progress: how many games each checked-in player still needs.
export function gamesTargetMeta(state) {
  const target = Number(state.settings?.gamesTarget) || 0;
  if (!target) return { target: 0, allReached: false, remaining: 0, players: [] };
  const checked = state.players.filter((p) => p.checkedIn);
  const players = checked.map((p) => ({ id: p.id, name: p.name, played: p.gamesPlayed || 0, target, done: (p.gamesPlayed || 0) >= target }));
  const allReached = checked.length > 0 && players.every((p) => p.done);
  const remaining = players.reduce((s, p) => s + Math.max(0, target - p.played), 0);
  return { target, allReached, remaining, players };
}

export function endSession() {
  return resetState();
}

// ranked standings — sort depends on the active scoring mode.
// includePaired=false drops any player who currently has a partner (Pair Ranking Inclusion OFF).
export function standings(state, includePaired = true) {
  const mode = state.settings?.scoringMode || "casual";
  const min = Number(state.settings?.minGamesToRank) || 0;
  const playing = playingPlayerIds(state);
  // Exclude the currently-in-progress (undecided) game from each player's games count —
  // it only counts toward standings once the match is finished.
  const effectiveGames = (p) => Math.max(0, (p.gamesPlayed || 0) - (playing.has(p.id) ? 1 : 0));
  const qual = (p) => ((p.gamesPlayed || 0) >= min ? 0 : 1); // 0 = ranked, 1 = below threshold (sinks to bottom) — p.gamesPlayed is already adjusted below
  const arr = state.players
    .filter((p) => includePaired || !p.partnerId)
    .filter(hasParticipated)
    .map(normalizePlayer)
    .map((p) => ({ ...p, gamesPlayed: effectiveGames(p) }));

  let cmp;
  if (mode === "advanced") {
    // Win Rate > PPM Differential > SDR > PPM Conceded (lowest first)
    cmp = (a, b) =>
      qual(a) - qual(b) ||
      b.winRate - a.winRate ||
      b.avgPPMDiff - a.avgPPMDiff ||
      b.avgSDR - a.avgSDR ||
      a.avgPPMC - b.avgPPMC ||
      b.wins - a.wins ||
      a.name.localeCompare(b.name);
  } else {
    // Win Rate > Strength of Schedule (Casual has no scores → no point differential)
    cmp = (a, b) =>
      qual(a) - qual(b) ||
      b.winRate - a.winRate ||
      b.strengthOfSchedule - a.strengthOfSchedule ||
      b.pointDifferential - a.pointDifferential ||
      b.wins - a.wins ||
      a.name.localeCompare(b.name);
  }
  const ranked = mergePairs(arr, state).sort(cmp);
  for (let i = 0; i < ranked.length - 1; i++) {
    const a = ranked[i], b = ranked[i + 1];
    if (qual(a) || qual(b) || a.winRate !== b.winRate) continue;
    ranked[i].tieNote = tieBreakNote(a, b, mode);
  }
  return ranked;
}

const pct = (v) => `${Math.round((v || 0) * 100)}%`;
const sgn = (v) => `${v > 0 ? "+" : ""}${Math.round((v || 0) * 100) / 100}`;
function tieBreakNote(a, b, mode) {
  const head = `Tied with ${b.name} on win rate (${pct(a.winRate)})`;
  const factors = mode === "advanced"
    ? [["PPM differential", "avgPPMDiff", sgn, 1], ["score-differential rate", "avgSDR", sgn, 1], ["fewer points conceded per minute", "avgPPMC", sgn, -1]]
    : [["strength of schedule", "strengthOfSchedule", (v) => `${Math.round(((v || 0) / 5) * 100)}%`, 1], ["point differential", "pointDifferential", sgn, 1]];
  factors.push(["more wins", "wins", (v) => `${v || 0}W`, 1]);
  const same = [];
  for (const [label, key, fmt, dir] of factors) {
    const x = a[key] || 0, y = b[key] || 0;
    if (x !== y && (x - y) * dir > 0) {
      return `${head}${same.length ? ` and ${same.join(", ")}` : ""} — ranks higher on ${label} (${fmt(x)} vs ${fmt(y)}).`;
    }
    same.push(`${label.replace(/^more |^fewer /, "")} (${fmt(x)})`);
  }
  return `${head} and every tie-breaker — listed alphabetically.`;
}

// Locked pairs (mutual partnerId) share one standings row: "A & B".
function mergePairs(arr, state) {
  const byId = new Map(arr.map((p) => [p.id, p]));
  const out = [], used = new Set();
  for (const p of arr) {
    if (used.has(p.id)) continue;
    const mate = p.partnerId ? byId.get(p.partnerId) : null;
    if (!mate || mate.partnerId !== p.id) { out.push(p); continue; }
    used.add(p.id); used.add(mate.id);
    const row = { ...p, id: p.id, memberIds: [p.id, mate.id], name: `${p.name} & ${mate.name}`, isPair: true };
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "number" && typeof mate[k] === "number") row[k] = (v + mate[k]) / 2;
    }
    for (const k of ["wins", "losses", "gamesPlayed"]) row[k] = Math.round(row[k] || 0);
    out.push(row);
  }
  return out;
}

// how many more games a player needs before they enter the ranked standings (0 = ranked)
export function gamesToRank(state, player) {
  const min = Number(state.settings?.minGamesToRank) || 0;
  return Math.max(0, min - (player?.gamesPlayed || 0));
}

// ---- selectors -------------------------------------------------------------
export function getPlayer(state, id) {
  return state.players.find((p) => p.id === id) || null;
}

export function playingPlayerIds(state) {
  const ids = new Set();
  state.courts.forEach((c) => {
    if (c.status === "in_use" && c.currentMatch) {
      [...c.currentMatch.teamA, ...c.currentMatch.teamB].forEach((id) => ids.add(id));
    }
  });
  return ids;
}

export function countCheckedIn(state) {
  return state.players.filter((p) => p.checkedIn).length;
}

export function countPlaying(state) {
  return playingPlayerIds(state).size;
}

export function restColor(player, now = Date.now()) {
  const rest = (now - (player.lastPlayedTimestamp || now)) / 1000;
  if (rest < REST_THRESHOLDS.justFinished) return "just_finished";
  if (rest < REST_THRESHOLDS.warmingUp) return "warming_up";
  return "ready";
}

// waiting pool = checked-in, present, not currently playing
export function waitingPool(state) {
  const playing = playingPlayerIds(state);
  return state.queue
    .map((id) => getPlayer(state, id))
    .filter((p) => p && p.checkedIn && !playing.has(p.id));
}

// ---- mutations (return NEW state, do not mutate) ---------------------------
export function addPlayer(state, rawName, skillLevel = 3) {
  const name = (rawName || "").trim().replace(/\s+/g, " ");
  if (!name) return { state, error: "Please enter a player name." };
  // prevent duplicate names on the roster (pending or checked-in)
  const dupe = state.players.find(
    (p) => p.name.toLowerCase() === name.toLowerCase()
  );
  if (dupe) return { state, error: `"${name}" is already on the roster.` };

  const now = Date.now();
  const player = {
    id: uid("player"),
    name,
    skillLevel: Number(skillLevel) || 3,
    checkedIn: false, // players are added as PENDING — organizer checks them in when ready
    checkedOut: false,
    gamesPlayed: 0,
    partnerId: null,
    lastPlayedTimestamp: now, // used as wait baseline
    ...PLAYER_METRIC_DEFAULTS,
  };
  const next = {
    ...state,
    players: [...state.players, player],
  };
  return { state: next, player };
}

// Open-play match history for a player NAME, scanned across all local sessions on this
// device (open play is local-first, so this is best-effort by name). Returns per-game
// detail + win-rate / PPM / point-differential.
export function localPlayerHistory(name) {
  const key = (name || "").trim().toLowerCase();
  const empty = { games: [], summary: { games: 0, wins: 0, losses: 0, winRate: 0, ppm: 0, differential: 0 } };
  if (!key) return empty;
  const games = [];
  let w = 0, l = 0, pf = 0, pa = 0, scored = 0;
  for (const id of readIndex()) {
    let s = null;
    try { s = JSON.parse(localStorage.getItem(stateKey(id))); } catch (e) { s = null; }
    if (!s || !Array.isArray(s.history)) continue;
    const label = s.session?.eventName || s.session?.location || "Open Play";
    for (const h of s.history) {
      const inA = (h.team_a || []).some((n) => (n || "").toLowerCase() === key);
      const inB = (h.team_b || []).some((n) => (n || "").toLowerCase() === key);
      if (!inA && !inB) continue;
      const onA = inA;
      const won = h.winner ? ((h.winner === "A") === onA) : null;
      const hasScore = h.score_a != null && h.score_b != null;
      const my = hasScore ? (onA ? h.score_a : h.score_b) : null;
      const opp = hasScore ? (onA ? h.score_b : h.score_a) : null;
      if (won === true) w += 1; else if (won === false) l += 1;
      if (hasScore) { pf += my; pa += opp; scored += 1; }
      games.push({
        event: label, playedAt: h.playedAt, win: won,
        partners: (onA ? h.team_a : h.team_b).filter((n) => (n || "").toLowerCase() !== key),
        opponents: onA ? h.team_b : h.team_a,
        points_for: my, points_against: opp, differential: hasScore ? my - opp : null,
      });
    }
  }
  games.sort((a, b) => (b.playedAt || "").localeCompare(a.playedAt || ""));
  const n = games.length;
  return { games, summary: { games: n, wins: w, losses: l, winRate: n ? w / n : 0,
    ppm: scored ? Math.round((pf / scored) * 10) / 10 : 0, differential: pf - pa } };
}

// Known players the organizer has played with before — aggregated across ALL of this
// device's past Open Play sessions (name, best-known skill, lifetime games & win rate).
// Used to power the add-player search box.
export function knownLocalPlayers() {
  const out = {};
  for (const id of readIndex()) {
    let s = null;
    try { s = JSON.parse(localStorage.getItem(stateKey(id))); } catch (e) { s = null; }
    if (!s || !Array.isArray(s.players)) continue;
    for (const p of s.players) {
      const key = (p.name || "").trim().toLowerCase();
      if (!key) continue;
      const e = out[key] || { name: (p.name || "").trim(), skillLevel: p.skillLevel || 3, games: 0, wins: 0, sessions: 0 };
      e.games += p.gamesPlayed || 0;
      e.wins += p.wins || 0;
      e.sessions += 1;
      if (p.skillLevel) e.skillLevel = p.skillLevel;
      out[key] = e;
    }
  }
  return Object.values(out).map((e) => ({ ...e, winRate: e.games ? e.wins / e.games : 0, source: "local" }));
}

// Bulk add — used by the Import Players modal (paste list / Reclub).
export function addPlayers(state, entries, defaultSkill = 3) {
  let next = state;
  let added = 0;
  const skipped = [];
  for (const e of entries || []) {
    const name = typeof e === "string" ? e : e?.name;
    const skill = (typeof e === "object" && e?.skillLevel) || defaultSkill;
    const res = addPlayer(next, name, skill);
    if (res.error) { if (name) skipped.push(name.trim()); continue; }
    next = res.state;
    added += 1;
  }
  return { state: next, added, skipped };
}

export function toggleCheckIn(state, id) {
  const p = getPlayer(state, id);
  if (!p) return { state };
  // can't check out a player who is mid-match
  if (p.checkedIn && playingPlayerIds(state).has(id)) {
    return { state, error: `${p.name} is on a court right now — stop or finish their match first.` };
  }
  const now = Date.now();
  const players = state.players.map((x) =>
    x.id === id
      ? { ...x, checkedIn: !x.checkedIn, checkedOut: x.checkedIn ? true : false, lastPlayedTimestamp: now }
      : x
  );
  let queue = state.queue;
  if (p.checkedIn) queue = queue.filter((q) => q !== id); // checking out
  else if (!queue.includes(id)) queue = [...queue, id];   // checking in / back in
  return { state: { ...state, players, queue } };
}

// Delete-while-checked-in: keep the player on record (checked-out log), preserve history & totals.
export function checkOutPlayer(state, id) {
  const p = getPlayer(state, id);
  if (!p) return { state, error: "Player not found." };
  if (playingPlayerIds(state).has(id)) {
    return { state, error: `${p.name} is on a court right now — stop or finish their match first.` };
  }
  const players = state.players.map((x) => (x.id === id ? { ...x, checkedIn: false, checkedOut: true } : x));
  const queue = state.queue.filter((q) => q !== id);
  return { state: { ...state, players, queue } };
}

export function removePlayer(state, id) {
  return {
    ...state,
    players: state.players
      .filter((p) => p.id !== id)
      .map((p) => (p.partnerId === id ? { ...p, partnerId: null } : p)),
    queue: state.queue.filter((q) => q !== id),
  };
}

// Persistent pairing: two players always share a team until unpaired.
export function setPartner(state, id, partnerId) {
  if (!id || !partnerId || id === partnerId) return state;
  const players = state.players.map((p) => {
    if (p.id === id) return { ...p, partnerId };
    if (p.id === partnerId) return { ...p, partnerId: id };
    // dissolve any prior links the two had with others
    if (p.partnerId === id || p.partnerId === partnerId) return { ...p, partnerId: null };
    return p;
  });
  return { ...state, players };
}

export function clearPartner(state, id) {
  const p = getPlayer(state, id);
  const pid = p?.partnerId;
  const players = state.players.map((x) => {
    if (x.id === id) return { ...x, partnerId: null };
    if (pid && x.id === pid) return { ...x, partnerId: null };
    return x;
  });
  return { ...state, players };
}

// edit a player's name / skill (with duplicate-name protection)
export function updatePlayer(state, id, { name, skillLevel }) {
  const clean = (name || "").trim().replace(/\s+/g, " ");
  if (!clean) return { state, error: "Name cannot be empty." };
  const dupe = state.players.find(
    (p) => p.id !== id && p.name.toLowerCase() === clean.toLowerCase()
  );
  if (dupe) return { state, error: `"${clean}" is already on the roster.` };
  const players = state.players.map((p) =>
    p.id === id ? { ...p, name: clean, skillLevel: Number(skillLevel) || p.skillLevel } : p
  );
  return { state: { ...state, players } };
}

// Reorder a flat lineup (length = needed) so manually-locked partners share a team half.
// Slots [0..half-1] = Team A, [half..needed-1] = Team B. Empty (null) slots trail at the end.
export function arrangeLineup(state, ids, needed) {
  const half = needed / 2;
  const filled = (ids || []).filter(Boolean);
  const seen = new Set();
  const pairs = [];
  const singles = [];
  for (const id of filled) {
    if (seen.has(id)) continue;
    const p = getPlayer(state, id);
    const mateId = p?.partnerId;
    if (mateId && filled.includes(mateId) && !seen.has(mateId) && getPlayer(state, mateId)?.partnerId === id) {
      pairs.push([id, mateId]);
      seen.add(id);
      seen.add(mateId);
      continue;
    }
    singles.push(id);
    seen.add(id);
  }
  const teamA = [];
  const teamB = [];
  for (const pr of pairs) {
    if (teamA.length === 0) teamA.push(...pr);
    else if (teamB.length === 0) teamB.push(...pr);
    else { if (teamA.length < half) teamA.push(pr[0]); if (teamB.length < half) teamB.push(pr[1]); }
  }
  for (const s of singles) {
    if (teamA.length < half) teamA.push(s);
    else if (teamB.length < half) teamB.push(s);
  }
  const out = [...teamA, ...teamB];
  while (out.length < needed) out.push(null);
  return out.slice(0, needed);
}

// true if teamA/teamB would split a manually-locked pair across opposing teams
function splitsLockedPair(state, teamA, teamB) {
  if (state.settings?.mode === "singles") return null;
  for (const id of teamA) {
    const p = getPlayer(state, id);
    if (p?.partnerId && teamB.includes(p.partnerId) && getPlayer(state, p.partnerId)?.partnerId === id) {
      return { a: p.name, b: getPlayer(state, p.partnerId)?.name };
    }
  }
  return null;
}

// simulate the next staged matches (one per available court) without mutating state
export function previewNextMatches(state) {
  let sim = JSON.parse(JSON.stringify(state));
  const previews = [];
  const availableIds = sim.courts.filter((c) => c.status === "available").map((c) => c.id);
  for (const cid of availableIds) {
    const res = stageNextMatch(sim, cid);
    if (res.error) break;
    sim = res.state;
    previews.push({
      courtId: res.staged.court.id,
      courtName: res.staged.court.name,
      teamA: res.staged.teamA,
      teamB: res.staged.teamB,
      teamAnames: res.staged.teamAnames,
      teamBnames: res.staged.teamBnames,
    });
  }
  return previews;
}

// stage an exact lineup (used by the editable Coming Up Next card / Stage Next)
export function stageLineup(state, courtId, ids) {
  const court = state.courts.find((c) => c.id === courtId);
  if (!court) return { state, error: "Court not found." };
  if (court.status === "in_use") return { state, error: "This court already has an active match." };
  const needed = state.settings.mode === "singles" ? 2 : 4;
  const clean = (ids || []).filter(Boolean);
  if (clean.length < needed) {
    return { state, error: `Insufficient players in queue to stage a match. Need ${needed}, have ${clean.length}. Autofill the empty slots first.` };
  }
  if (new Set(clean).size !== clean.length) return { state, error: "The same player is listed twice in this match." };
  const playing = playingPlayerIds(state);
  for (const id of clean) {
    const p = getPlayer(state, id);
    if (!p || !p.checkedIn) return { state, error: "A selected player is no longer checked in." };
    if (playing.has(id)) return { state, error: `${p.name} is already playing on another court.` };
  }
  const half = needed / 2;
  const teamA = clean.slice(0, half);
  const teamB = clean.slice(half, needed);
  const split = splitsLockedPair(state, teamA, teamB);
  if (split) return { state, error: `${split.a} & ${split.b} are a locked pair — they must stay on the same team. Unpair them first or fix the line-up.` };
  const selectedIds = new Set([...teamA, ...teamB]);
  const now = Date.now();
  const players = state.players.map((p) => {
    if (!selectedIds.has(p.id)) return p;
    const mate = teamA.includes(p.id) ? teamA.find((i) => i !== p.id) : teamB.find((i) => i !== p.id);
    return {
      ...p,
      gamesPlayed: p.gamesPlayed + 1,
      lastPartnerId: mate || null,
      partnerHistoryIds: mate ? [...(p.partnerHistoryIds || []), mate] : (p.partnerHistoryIds || []),
    };
  });
  const courts = state.courts.map((c) =>
    c.id === courtId ? { ...c, status: "in_use", currentMatch: { teamA, teamB }, startTime: now } : c
  );
  const queue = state.queue.filter((id) => !selectedIds.has(id));
  const nameOf = (id) => state.players.find((p) => p.id === id)?.name || "";
  return {
    state: { ...state, players, courts, queue, pendingTogether: [] },
    staged: {
      court, teamA, teamB,
      teamAnames: teamA.map(nameOf), teamBnames: teamB.map(nameOf),
      mode: state.settings.mode,
    },
  };
}

// Stop the current game with NO bearing on standings (undo the staging counters and requeue)
export function cancelMatch(state, courtId) {
  const court = state.courts.find((c) => c.id === courtId);
  if (!court || court.status !== "in_use" || !court.currentMatch) {
    return { state, error: "No active match on this court." };
  }
  const ids = [...court.currentMatch.teamA, ...court.currentMatch.teamB];
  const now = Date.now();
  const players = state.players.map((p) => {
    if (!ids.includes(p.id)) return p;
    const np = normalizePlayer(p);
    np.gamesPlayed = Math.max(0, (np.gamesPlayed || 0) - 1);
    np.partnerHistoryIds = (np.partnerHistoryIds || []).slice(0, -1);
    np.winRate = np.gamesPlayed > 0 ? np.wins / np.gamesPlayed : 0;
    // STOP: keep their pre-stage wait baseline so they rank FIRST (top) on next staging.
    return np;
  });
  const courts = state.courts.map((c) =>
    c.id === courtId ? { ...c, status: "available", currentMatch: null, startTime: null } : c
  );
  const stillIn = (id) => players.find((pp) => pp.id === id)?.checkedIn && !state.queue.includes(id);
  // STOP (not finish): players jump to the TOP of the queue so they're staged again first.
  const queue = [...ids.filter(stillIn), ...state.queue];
  return { state: { ...state, players, courts, queue } };
}

// Swap the lineup of an already-started match (organizer correction window).
// Keeps the same court/startTime; players removed from the match are re-queued at the top,
// newly added players are pulled from the queue. Game counters are adjusted to stay accurate.
export function editActiveLineup(state, courtId, ids) {
  const court = state.courts.find((c) => c.id === courtId);
  if (!court || court.status !== "in_use" || !court.currentMatch) {
    return { state, error: "No active match on this court." };
  }
  const needed = state.settings.mode === "singles" ? 2 : 4;
  const clean = (ids || []).filter(Boolean);
  if (clean.length !== needed) return { state, error: `A ${state.settings.mode} match needs exactly ${needed} players.` };
  if (new Set(clean).size !== clean.length) return { state, error: "The same player is listed twice." };
  const playing = playingPlayerIds(state);
  for (const id of clean) {
    const p = getPlayer(state, id);
    if (!p || !p.checkedIn) return { state, error: "A selected player is not checked in." };
    // allow players already on THIS court; block players busy on OTHER courts
    const onThisCourt = [...court.currentMatch.teamA, ...court.currentMatch.teamB].includes(id);
    if (playing.has(id) && !onThisCourt) return { state, error: `${p.name} is already playing on another court.` };
  }
  const prevIds = [...court.currentMatch.teamA, ...court.currentMatch.teamB];
  const removed = prevIds.filter((id) => !clean.includes(id));
  const added = clean.filter((id) => !prevIds.includes(id));
  const half = needed / 2;
  const teamA = clean.slice(0, half);
  const teamB = clean.slice(half, needed);
  const split = splitsLockedPair(state, teamA, teamB);
  if (split) return { state, error: `${split.a} & ${split.b} are a locked pair — they must stay on the same team.` };
  const sel = new Set(clean);

  const players = state.players.map((p) => {
    // players removed from the match: undo their staging counters + re-open partner memory
    if (removed.includes(p.id)) {
      const np = normalizePlayer(p);
      np.gamesPlayed = Math.max(0, (np.gamesPlayed || 0) - 1);
      np.partnerHistoryIds = (np.partnerHistoryIds || []).slice(0, -1);
      // removed via correction: keep prior wait baseline so they re-stage first
      return np;
    }
    if (!sel.has(p.id)) return p;
    const mate = teamA.includes(p.id) ? teamA.find((i) => i !== p.id) : teamB.find((i) => i !== p.id);
    if (added.includes(p.id)) {
      // newly added: count a game + record the new partner
      return {
        ...p,
        gamesPlayed: (p.gamesPlayed || 0) + 1,
        lastPartnerId: mate || null,
        partnerHistoryIds: mate ? [...(p.partnerHistoryIds || []), mate] : (p.partnerHistoryIds || []),
      };
    }
    // stayed on court: just refresh partner memory to the (possibly new) mate
    return { ...p, lastPartnerId: mate || null };
  });

  const courts = state.courts.map((c) =>
    c.id === courtId ? { ...c, currentMatch: { teamA, teamB } } : c
  );
  // removed players go back to the TOP of the queue; added players leave it
  const queue = [...removed, ...state.queue.filter((id) => !sel.has(id))];
  return { state: { ...state, players, courts, queue } };
}

export function setSettings(state, patch) {
  return { ...state, settings: { ...state.settings, ...patch } };
}

export function addCourt(state) {
  return {
    ...state,
    courts: [
      ...state.courts,
      { id: uid("court"), name: `Court ${state.courts.length + 1}`, status: "available", currentMatch: null, startTime: null },
    ],
  };
}

export function removeCourt(state, courtId) {
  const court = state.courts.find((c) => c.id === courtId);
  if (court && court.status === "in_use") return { state, error: "Finish the match before removing this court." };
  return { state: { ...state, courts: state.courts.filter((c) => c.id !== courtId) } };
}

export function renameCourt(state, courtId, name) {
  const clean = (name || "").trim().slice(0, 40);
  if (!clean) return { state, error: "Court name can't be empty." };
  if (state.courts.some((c) => c.id !== courtId && c.name.trim().toLowerCase() === clean.toLowerCase()))
    return { state, error: "Another court already has that name." };
  return { state: { ...state, courts: state.courts.map((c) => (c.id === courtId ? { ...c, name: clean } : c)) } };
}

// FAIRNESS / SIT-TO-PLAY ENGINE (rotation + partner aware)
// how MANY times two players have already partnered / faced each other (for even spreading)
function partnerTimes(a, b) {
  return (a.partnerHistoryIds || []).filter((id) => id === b.id).length;
}
function oppTimes(a, b) {
  return (a.opponentHistoryIds || []).filter((id) => id === b.id).length;
}
// consecutive (last match) repeats — weighted heavily so back-to-back repeats are avoided
function lastPartnerRepeat(a, b) {
  return a.lastPartnerId === b.id || b.lastPartnerId === a.id;
}
function lastOppRepeat(a, b) {
  return (a.lastOpponentIds || []).includes(b.id) || (b.lastOpponentIds || []).includes(a.id);
}

// cost of a proposed line-up — higher = more repeats, so lower is more diverse.
// Uses REPEAT COUNTS (not just seen/not-seen) so the engine spreads play out evenly:
// brand-new partners/opponents cost 0, and the more times a pairing has already happened
// the more it's penalised — pushing the rotation toward "everyone plays with everyone".
function splitCost(teamA, teamB) {
  let cost = 0;
  const partnerPenalty = (pair) => {
    if (pair.length < 2) return 0;
    const [x, y] = pair;
    if (lastPartnerRepeat(x, y)) return 240;        // back-to-back same partner — avoid hardest
    return partnerTimes(x, y) * 60;                  // 1st repeat = 60 (≈3 queue slots), grows each time
  };
  cost += partnerPenalty(teamA) + partnerPenalty(teamB);
  for (const x of teamA) for (const y of teamB) {
    if (lastOppRepeat(x, y)) cost += 150;            // back-to-back same opponent
    else cost += oppTimes(x, y) * 25;                // grows with every prior meeting
  }
  return cost;
}

// enumerate valid team splits for a chosen set of units and return the best one.
// locked pairs (2-player units) are NEVER split apart. When `balanced` is true the
// primary objective is an even skill split (fairest game), with repeat-avoidance as
// the tie-breaker; otherwise diversity (fewest repeats) is the sole objective.
function teamSkill(team) { return team.reduce((s, p) => s + (p.skillLevel || 3), 0); }
function bestSplit(units, needed, balanced = false) {
  const lockedPairs = units.filter((u) => u.length === 2);
  const singles = units.filter((u) => u.length === 1).map((u) => u[0]);
  const candidates = [];
  if (needed === 2) {
    if (singles.length >= 2) candidates.push([[singles[0]], [singles[1]]]);
  } else if (lockedPairs.length === 2) {
    candidates.push([lockedPairs[0], lockedPairs[1]]);
  } else if (lockedPairs.length === 1 && singles.length >= 2) {
    candidates.push([lockedPairs[0], singles.slice(0, 2)]);
  } else if (singles.length >= 4) {
    const [a, b, c, d] = singles;
    candidates.push([[a, b], [c, d]]);
    candidates.push([[a, c], [b, d]]);
    candidates.push([[a, d], [b, c]]);
  }
  let best = null;
  for (const [teamA, teamB] of candidates) {
    if (!teamA || !teamB || teamA.some((x) => !x) || teamB.some((x) => !x)) continue;
    const cost = splitCost(teamA, teamB);
    const imb = Math.abs(teamSkill(teamA) - teamSkill(teamB));
    const score = balanced ? imb * 1000 + cost : cost;
    if (!best || score < best.score) best = { teamA, teamB, cost, imb, score };
  }
  return best;
}

// Open Play + : build the most DIVERSE valid line-up from the priority-ordered pool.
//  • manually locked pairs are NEVER broken — a locked player waits if their partner isn't available;
//  • prefers longest-waiting players (fairness) but will bump a player a slot or two down the queue
//    to avoid repeating the PREVIOUS match's partners / opponents.
function buildOpenPlayPlusTeams(order, needed, balanced = false) {
  const doubles = needed >= 4;
  // 1. atomic units in priority order — locked pairs stay together, locked-without-mate wait
  const units = [];
  const consumed = new Set();
  for (const p of order) {
    if (consumed.has(p.id)) continue;
    if (doubles && p.partnerId) {
      const mate = order.find((x) => x.id === p.partnerId && !consumed.has(x.id));
      if (mate && mate.partnerId === p.id) {
        units.push([p, mate]);
        consumed.add(p.id);
        consumed.add(mate.id);
      } else {
        consumed.add(p.id); // locked partner not in pool → this player waits (pair preserved)
      }
      continue;
    }
    units.push([p]);
    consumed.add(p.id);
  }

  // 2. fairness window: top players + a small look-ahead so diversity swaps don't starve the queue
  const window = [];
  let count = 0;
  for (const u of units) {
    window.push(u);
    count += u.length;
    if (count >= needed + 6) break;
  }

  // 3. enumerate unit-subsets that sum to exactly `needed`
  const subsets = [];
  const rec = (i, chosen, c) => {
    if (c === needed) { subsets.push(chosen); return; }
    if (c > needed || i >= window.length) return;
    if (c + window[i].length <= needed) rec(i + 1, [...chosen, i], c + window[i].length);
    rec(i + 1, chosen, c);
  };
  rec(0, [], 0);
  if (!subsets.length) {
    return { error: "Insufficient players to form two full teams with the current pairings." };
  }

  // 4. pick the subset+split with the best balance of fairness (priority) and diversity (repeats).
  // Balanced ON → skill-even split dominates, then diversity, then queue fairness.
  // Balanced OFF → maximize unique partners/opponents (diversity) with queue fairness.
  let best = null;
  for (const idxs of subsets) {
    const sub = idxs.map((i) => window[i]);
    const split = bestSplit(sub, needed, balanced);
    if (!split) continue;
    const prioCost = idxs.reduce((s, i) => s + i, 0); // lower index = longer waiting
    const total = balanced
      ? split.imb * 1000 + split.cost + prioCost * 2
      : prioCost * 20 + split.cost;
    if (!best || total < best.total) best = { total, split };
  }
  if (!best) {
    return { error: "Insufficient players to form two full teams with the current pairings." };
  }
  return { teamA: best.split.teamA.map((p) => p.id), teamB: best.split.teamB.map((p) => p.id) };
}

// Explains any repeat partnership / opponent match-up on a proposed line-up (Coming Up Next badge).
// Returns { repeat, lines: [{type, text}], reason } — lines name the players and how many times
// they've already partnered / been matched, so the organizer can decide whether to override.
export function pairingNote(state, teamA = [], teamB = [], manualOverride = false) {
  if ((state.settings?.rotation || "") !== "open_play_plus") return null;
  const pa = teamA.map((id) => getPlayer(state, id)).filter(Boolean);
  const pb = teamB.map((id) => getPlayer(state, id)).filter(Boolean);
  // A pair the organizer manually locked together (partnerId ↔) is intentional — never warn.
  const isLocked = (pair) => pair.length === 2 && pair[0].partnerId === pair[1].id && pair[1].partnerId === pair[0].id;
  const times = (n) => `${n}×`;
  const lines = [];
  const partnerLine = (pair) => {
    if (pair.length < 2 || isLocked(pair)) return;
    const t = partnerTimes(pair[0], pair[1]);
    if (t > 0) lines.push({ type: "partner", text: `Repeat partner: ${pair[0].name} & ${pair[1].name} — partnered ${times(t)} already` });
  };
  partnerLine(pa);
  partnerLine(pb);
  for (const x of pa) for (const y of pb) {
    const t = oppTimes(x, y);
    if (t > 0) lines.push({ type: "opponent", text: `Repeat opponent: ${x.name} vs ${y.name} — matched ${times(t)} before` });
  }
  if (!lines.length) return null;
  if (manualOverride) {
    return { repeat: true, manual: true, lines: [{ type: "manual", text: "Manual override — line-up set by the organizer." }], reason: "Manual override — line-up set by the organizer." };
  }
  return { repeat: true, lines, reason: lines.map((l) => l.text).join("; ") };
}

export function stageNextMatch(state, courtId) {
  const court = state.courts.find((c) => c.id === courtId);
  if (!court) return { state, error: "Court not found." };
  if (court.status === "in_use") return { state, error: "This court already has an active match." };

  const rotation = state.settings.rotation || "open_play";
  const needed = state.settings.mode === "singles" ? 2 : 4;

  // Step 1: eligible pool
  const pool = waitingPool(state);
  if (pool.length < needed) {
    return { state, error: `Insufficient players in queue to stage a match. Need ${needed}, have ${pool.length}.` };
  }

  const now = Date.now();
  // Step 2: ordering — random for numbering, otherwise weighted rest priority
  const target = Number(state.settings.gamesTarget) || 0;
  let priorityOrder;
  if (rotation === "numbering") {
    priorityOrder = [...pool].sort(() => Math.random() - 0.5);
  } else if (target > 0) {
    // Games-target mode: everyone must reach `target` games fairly.
    // Below-target players first, fewest games first (late check-ins catch up),
    // then longest-rested. Players who already hit target only fill if needed.
    priorityOrder = pool
      .map((p) => ({ p, rest: (now - (p.lastPlayedTimestamp || now)), played: p.gamesPlayed || 0, done: (p.gamesPlayed || 0) >= target }))
      .sort((a, b) => (a.done - b.done) || (a.played - b.played) || (b.rest - a.rest))
      .map((s) => s.p);
  } else {
    priorityOrder = pool
      .map((p) => ({ p, score: (now - (p.lastPlayedTimestamp || now)) * FAIRNESS_WEIGHT }))
      .sort((a, b) => b.score - a.score)
      .map((s) => s.p);
  }

  // challenge-court "stay" winners forced onto a team
  const forced = (state.pendingTogether || [])
    .map((id) => pool.find((p) => p.id === id))
    .filter(Boolean);

  let teamA = [];
  let teamB = [];

  if (rotation === "open_play_plus" && !forced.length) {
    // Open Play + : queue priority + unique partnerships/opponents, locked pairs never broken.
    // Balanced toggle makes skill-even teams the priority (diversity becomes the tie-break).
    const built = buildOpenPlayPlusTeams(priorityOrder, needed, !!state.settings.balanced);
    if (built.error) return { state, error: built.error };
    teamA = built.teamA;
    teamB = built.teamB;
  } else if (state.settings.mode === "singles") {
    if (forced.length) {
      teamA = [forced[0].id];
      const opp = priorityOrder.find((p) => p.id !== forced[0].id);
      teamB = [opp.id];
    } else {
      teamA = [priorityOrder[0].id];
      teamB = [priorityOrder[1].id];
    }
  } else {
    const teams = [[], []];
    const used = new Set();

    // seat forced (challenge-court) winners on team A
    if (forced.length) {
      teams[0] = forced.slice(0, 2);
      teams[0].forEach((p) => used.add(p.id));
    }

    // build pair/single units from the rest, strictly honouring locked partners
    const rest = priorityOrder.filter((p) => !used.has(p.id));
    const pairs = [];
    const singles = [];
    for (const p of rest) {
      if (used.has(p.id)) continue;
      if (p.partnerId) {
        const partner = rest.find((x) => x.id === p.partnerId && !used.has(x.id));
        if (partner) {
          pairs.push([p, partner]);
          used.add(p.id);
          used.add(partner.id);
        } else {
          used.add(p.id); // locked partner unavailable → wait, never break the pair
        }
        continue;
      }
      singles.push(p);
      used.add(p.id);
    }
    // round robin: avoid re-pairing a player with their most recent partner
    let orderedSingles = singles;
    if (rotation === "round_robin") {
      orderedSingles = [...singles].sort((a, b) => {
        const ap = teams[0][0] && a.lastPartnerId === teams[0][0].id ? 1 : 0;
        const bp = teams[0][0] && b.lastPartnerId === teams[0][0].id ? 1 : 0;
        return ap - bp;
      });
    }
    for (const pr of pairs) {
      const ti = teams.findIndex((t) => t.length === 0);
      if (ti !== -1) teams[ti] = [pr[0], pr[1]];
    }
    for (const s of orderedSingles) {
      const ti = teams.findIndex((t) => t.length < 2);
      if (ti === -1) break;
      teams[ti].push(s);
    }
    if (teams[0].length < 2 || teams[1].length < 2) {
      return { state, error: "Insufficient players to form two full teams with the current pairings." };
    }
    // balanced skill: pair strongest with weakest so both teams are even.
    // Applies to every rotation except Open Play + (which balances in its own engine),
    // when there are no locked pairs or forced "stay" winners to honour.
    if (state.settings.balanced && rotation !== "open_play_plus" && pairs.length === 0 && !forced.length) {
      const four = [...teams[0], ...teams[1]].sort((a, b) => b.skillLevel - a.skillLevel);
      teams[0] = [four[0], four[3]];
      teams[1] = [four[1], four[2]];
    }
    teamA = teams[0].map((p) => p.id);
    teamB = teams[1].map((p) => p.id);
  }

  const selectedIds = new Set([...teamA, ...teamB]);

  // Step 5: move to court, mark in_use, update counters + recent-partner memory
  const partnerA = teamA;
  const partnerB = teamB;
  const players = state.players.map((p) => {
    if (!selectedIds.has(p.id)) return p;
    const mate = partnerA.includes(p.id)
      ? partnerA.find((id) => id !== p.id)
      : partnerB.find((id) => id !== p.id);
    return {
      ...p,
      gamesPlayed: p.gamesPlayed + 1,
      lastPartnerId: mate || null,
      partnerHistoryIds: mate ? [...(p.partnerHistoryIds || []), mate] : (p.partnerHistoryIds || []),
    };
  });
  const courts = state.courts.map((c) =>
    c.id === courtId
      ? { ...c, status: "in_use", currentMatch: { teamA, teamB }, startTime: now }
      : c
  );
  const queue = state.queue.filter((id) => !selectedIds.has(id));

  const nameOf = (id) => state.players.find((p) => p.id === id)?.name || "";
  return {
    state: { ...state, players, courts, queue, pendingTogether: [] },
    staged: {
      court,
      teamA,
      teamB,
      teamAnames: teamA.map(nameOf),
      teamBnames: teamB.map(nameOf),
      mode: state.settings.mode,
    },
  };
}

export function finishMatch(state, courtId, result = {}) {
  const court = state.courts.find((c) => c.id === courtId);
  if (!court || court.status !== "in_use" || !court.currentMatch) {
    return { state, error: "No active match on this court." };
  }

  // backward-compat: allow a plain winner string
  if (typeof result === "string" || result === null) result = { winner: result };
  const advanced = (state.settings?.scoringMode || "casual") === "advanced";
  const rotation = state.settings.rotation || "open_play";
  const now = Date.now();
  const durationSeconds = court.startTime ? Math.round((now - court.startTime) / 1000) : 0;

  const teamAids = court.currentMatch.teamA;
  const teamBids = court.currentMatch.teamB;
  const ids = [...teamAids, ...teamBids];

  const scoreA = Number.isFinite(Number(result.scoreA)) && result.scoreA !== "" ? Number(result.scoreA) : null;
  const scoreB = Number.isFinite(Number(result.scoreB)) && result.scoreB !== "" ? Number(result.scoreB) : null;
  const hasScores = scoreA !== null && scoreB !== null;

  // winner derived from scores when available, else from explicit param
  let winner = result.winner || null;
  if (hasScores) winner = scoreA === scoreB ? null : scoreA > scoreB ? "A" : "B";

  const winIds = winner === "A" ? teamAids : winner === "B" ? teamBids : [];
  const loseIds = winner === "A" ? teamBids : winner === "B" ? teamAids : [];

  // duration in minutes for pacing — capped at 1 minute (zero-duration safeguard)
  const durMinutes = Math.max(1, advanced
    ? (Number(result.durationMinutes) || durationSeconds / 60)
    : durationSeconds / 60);

  const skillOf = (id) => getPlayer(state, id)?.skillLevel || 3;

  const players = state.players.map((p) => {
    if (!ids.includes(p.id)) return p;
    const np = normalizePlayer(p);
    const onA = teamAids.includes(p.id);
    const opponents = onA ? teamBids : teamAids;
    const teamScore = hasScores ? (onA ? scoreA : scoreB) : 0;
    const oppScore = hasScores ? (onA ? scoreB : scoreA) : 0;
    const won = winner && ((winner === "A") === onA);
    const games = np.gamesPlayed || 1; // already incremented at staging

    // --- core metrics (both modes) ---
    np.wins = (np.wins || 0) + (won ? 1 : 0);
    np.losses = (np.losses || 0) + (winner && !won ? 1 : 0);
    np.pointsScored = (np.pointsScored || 0) + teamScore;
    np.pointsConceded = (np.pointsConceded || 0) + oppScore;
    np.pointDifferential = np.pointsScored - np.pointsConceded;
    np.winRate = games > 0 ? np.wins / games : 0;

    // strength of schedule (running weighted average of opponent skill)
    const oppSkillAvg = opponents.length
      ? opponents.reduce((s, id) => s + skillOf(id), 0) / opponents.length
      : 0;
    np.strengthOfSchedule = ((np.strengthOfSchedule || 0) * (games - 1) + oppSkillAvg) / games;
    np.opponentHistoryIds = [...(np.opponentHistoryIds || []), ...opponents];
    np.lastOpponentIds = [...opponents];

    // --- advanced pacing metrics ---
    if (advanced && hasScores) {
      const ppmScored = teamScore / durMinutes;
      const ppmConceded = oppScore / durMinutes;
      const ppmDiff = ppmScored - ppmConceded;
      const sdr = (teamScore - oppScore) / durMinutes;
      np.avgPPMDiff = ((np.avgPPMDiff || 0) * (games - 1) + ppmDiff) / games;
      np.avgSDR = ((np.avgSDR || 0) * (games - 1) + sdr) / games;
      np.avgPPMC = ((np.avgPPMC || 0) * (games - 1) + ppmConceded) / games;
      np.totalMatchMinutesPlayed = (np.totalMatchMinutesPlayed || 0) + durMinutes;
    }

    np.lastPlayedTimestamp = now;
    return np;
  });

  const courts = state.courts.map((c) =>
    c.id === courtId ? { ...c, status: "available", currentMatch: null, startTime: null } : c
  );

  const stillIn = (id) => players.find((pp) => pp.id === id)?.checkedIn && !state.queue.includes(id);

  // rotation-specific re-queue behaviour
  let queue;
  let pendingTogether = [];
  if (winner && rotation === "king_of_court") {
    queue = [...winIds.filter(stillIn), ...state.queue, ...loseIds.filter(stillIn)];
  } else if (winner && rotation === "challenge_court") {
    queue = [...winIds.filter(stillIn), ...state.queue, ...loseIds.filter(stillIn)];
    pendingTogether = winIds.filter((id) => players.find((p) => p.id === id)?.checkedIn);
  } else {
    const requeue = ids.filter(stillIn);
    queue = [...state.queue, ...requeue];
  }

  const teamAnames = teamAids.map((id) => getPlayer(state, id)?.name).filter(Boolean);
  const teamBnames = teamBids.map((id) => getPlayer(state, id)?.name).filter(Boolean);
  const finished = {
    court_name: court.name,
    mode: state.settings.mode,
    team_a: teamAnames,
    team_b: teamBnames,
    duration_seconds: durationSeconds,
    winner: winner || null,
    winners: winner === "A" ? teamAnames : winner === "B" ? teamBnames : [],
    score_a: hasScores ? scoreA : null,
    score_b: hasScores ? scoreB : null,
    duration_minutes: advanced ? Math.round(durMinutes * 10) / 10 : null,
  };
  const record = { id: uid("match"), ...finished, team_a_ids: teamAids, team_b_ids: teamBids, playedAt: now };
  const history = [record, ...(state.history || [])];

  return {
    state: { ...state, players, courts, queue, pendingTogether, history },
    finished,
  };
}

// Recompute win/score-derived stats for every player from the full match history.
// Only touches result metrics (wins/losses/points/winRate + advanced PPM); pairing history,
// gamesPlayed, SoS and rest baselines are left untouched (they don't depend on who won).
export function recomputeResultStats(state) {
  const hist = state.history || [];
  const acc = {};
  state.players.forEach((p) => { acc[p.id] = { wins: 0, losses: 0, ps: 0, pc: 0, ppmDiffs: [], sdrs: [], ppmcs: [], minutes: 0 }; });
  for (const m of hist) {
    const aIds = m.team_a_ids || [];
    const bIds = m.team_b_ids || [];
    if (!aIds.length && !bIds.length) continue; // legacy record without ids — cannot recompute
    const hasScores = m.score_a != null && m.score_b != null;
    const dur = Math.max(1, Number(m.duration_minutes) || 0);
    const scored = hasScores && Number(m.duration_minutes) > 0;
    const winA = m.winner === "A";
    const winB = m.winner === "B";
    const apply = (ids, my, opp, won, lost) => {
      for (const id of ids) {
        const a = acc[id];
        if (!a) continue;
        if (won) a.wins += 1;
        if (lost) a.losses += 1;
        a.ps += my;
        a.pc += opp;
        if (scored) {
          const ppmS = my / dur, ppmC = opp / dur;
          a.ppmDiffs.push(ppmS - ppmC);
          a.sdrs.push((my - opp) / dur);
          a.ppmcs.push(ppmC);
          a.minutes += dur;
        }
      }
    };
    apply(aIds, hasScores ? m.score_a : 0, hasScores ? m.score_b : 0, winA, winB);
    apply(bIds, hasScores ? m.score_b : 0, hasScores ? m.score_a : 0, winB, winA);
  }
  const mean = (arr) => (arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : 0);
  const players = state.players.map((p) => {
    const a = acc[p.id];
    if (!a) return p;
    const np = normalizePlayer(p);
    const games = np.gamesPlayed || 0;
    np.wins = a.wins;
    np.losses = a.losses;
    np.pointsScored = a.ps;
    np.pointsConceded = a.pc;
    np.pointDifferential = a.ps - a.pc;
    np.winRate = games > 0 ? a.wins / games : 0;
    np.avgPPMDiff = mean(a.ppmDiffs);
    np.avgSDR = mean(a.sdrs);
    np.avgPPMC = mean(a.ppmcs);
    np.totalMatchMinutesPlayed = a.minutes;
    return np;
  });
  return { ...state, players };
}

// Flip a logged match's winner (fixes a mis-tap). Swaps the two team scores too so
// the result stays consistent, then recomputes affected player stats from history.
export function swapWinner(state, matchId) {
  const idx = (state.history || []).findIndex((m) => m.id === matchId);
  if (idx === -1) return { state, error: "Match not found." };
  const m = state.history[idx];
  if (m.winner !== "A" && m.winner !== "B") {
    return { state, error: "This match has no recorded winner to swap." };
  }
  const newWinner = m.winner === "A" ? "B" : "A";
  const hasScores = m.score_a != null && m.score_b != null;
  const updated = {
    ...m,
    winner: newWinner,
    score_a: hasScores ? m.score_b : m.score_a,
    score_b: hasScores ? m.score_a : m.score_b,
    winners: newWinner === "A" ? (m.team_a || []) : (m.team_b || []),
  };
  const history = state.history.map((rec, i) => (i === idx ? updated : rec));
  const recomputed = recomputeResultStats({ ...state, history });
  return { state: recomputed };
}

// Fairness report: how evenly partners & opponents have been distributed this session.
export function fairnessReport(state) {
  const rows = state.players.filter(hasParticipated).map((p) => {
    const np = normalizePlayer(p);
    const partners = np.partnerHistoryIds || [];
    const opponents = np.opponentHistoryIds || [];
    const uniquePartners = new Set(partners).size;
    const uniqueOpponents = new Set(opponents).size;
    const partnerDiversity = partners.length ? uniquePartners / partners.length : 1;
    const opponentDiversity = opponents.length ? uniqueOpponents / opponents.length : 1;
    return {
      id: np.id,
      name: np.name,
      gamesPlayed: np.gamesPlayed || 0,
      uniquePartners,
      totalPartnerings: partners.length,
      repeatPartners: partners.length - uniquePartners,
      uniqueOpponents,
      totalOpponentings: opponents.length,
      repeatOpponents: opponents.length - uniqueOpponents,
      partnerDiversity,
      opponentDiversity,
      diversity: (partnerDiversity + opponentDiversity) / 2,
    };
  });
  rows.sort((a, b) => a.diversity - b.diversity || a.name.localeCompare(b.name)); // least-diverse first (needs attention)
  const played = rows.filter((r) => r.totalPartnerings > 0 || r.totalOpponentings > 0);
  const avg = (key) => (played.length ? played.reduce((s, r) => s + r[key], 0) / played.length : 1);
  return {
    rows,
    overall: {
      partnerDiversity: avg("partnerDiversity"),
      opponentDiversity: avg("opponentDiversity"),
      score: (avg("partnerDiversity") + avg("opponentDiversity")) / 2,
      totalRepeatPartners: rows.reduce((s, r) => s + r.repeatPartners, 0),
      totalRepeatOpponents: rows.reduce((s, r) => s + r.repeatOpponents, 0),
    },
  };
}
